// Copyright (c) Ping. All rights reserved.
//
// Pure, framework-free fold of the `com.ping.meeting*` event stream into
// materialized meeting + RSVP state (types-and-schemas.md §2.13). Mirrors the
// iOS `MeetingStateStore` reducer rules byte-for-byte so both platforms
// converge to the same card state from the same events:
//
//   • create: upsert; a replayed / out-of-order create never clobbers a later
//     update (`updatedAtMs` LWW guard); absorbing-cancel wins.
//   • update / cancel: creator-only — the caller passes the MLS-AUTHENTICATED
//     sender account, never in-payload identity. Update is a partial diff
//     (absent = unchanged, empty string clears location/description/link).
//   • cancel: absorbing — once canceled, no later event resurrects the meeting.
//   • rsvp: last-writer-wins per (meeting_id, voter) on `votedAtMs`.
//
// The reducer is immutable (returns a new state object only when something
// changed) so it drops straight into a React `useMemo` projection.

import type {
  MeetingAutoCall,
  MeetingCancelBody,
  MeetingCreateBody,
  MeetingRsvpBody,
  MeetingRsvpResponse,
  MeetingUpdateBody,
} from '../appEvent/types';

export interface MeetingRecord {
  readonly meetingId: string;
  readonly creatorAccountId: string;
  readonly title: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly location?: string;
  readonly description?: string;
  readonly meetingLink?: string;
  readonly attendeeIds: ReadonlyArray<string>;
  readonly autoCall?: MeetingAutoCall;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  readonly canceled: boolean;
  readonly cancelReason?: string;
}

export interface MeetingRsvp {
  readonly meetingId: string;
  readonly voterAccountId: string;
  readonly response: MeetingRsvpResponse;
  readonly note?: string;
  readonly votedAtMs: number;
}

export interface MeetingState {
  readonly meetings: Readonly<Record<string, MeetingRecord>>;
  /** meetingId → voterAccountId → their latest RSVP. */
  readonly rsvps: Readonly<Record<string, Readonly<Record<string, MeetingRsvp>>>>;
}

export const EMPTY_MEETING_STATE: MeetingState = { meetings: {}, rsvps: {} };

/** A well-formed `meeting_id`: exactly 32 lowercase hex chars (16 bytes). */
export function isValidMeetingId(id: string): boolean {
  return /^[0-9a-f]{32}$/.test(id);
}

export type MeetingEvent =
  | { readonly kind: 'create'; readonly body: MeetingCreateBody }
  | { readonly kind: 'update'; readonly body: MeetingUpdateBody }
  | { readonly kind: 'rsvp'; readonly body: MeetingRsvpBody }
  | { readonly kind: 'cancel'; readonly body: MeetingCancelBody };

export interface MeetingEventMeta {
  /** MLS-authenticated sender account id (never the payload). */
  readonly senderAccountId: string;
  /** Event HLC wall-clock ms (LWW ordering key). */
  readonly at: number;
}

function applyCreate(
  state: MeetingState,
  body: MeetingCreateBody,
  meta: MeetingEventMeta,
): MeetingState {
  if (
    !isValidMeetingId(body.meeting_id) ||
    body.title.trim().length === 0 ||
    body.ends_at < body.starts_at ||
    meta.senderAccountId.length === 0
  ) {
    return state;
  }
  const existing = state.meetings[body.meeting_id];
  if (existing !== undefined) {
    // Absorbing cancel; and a replayed create never clobbers newer state.
    if (existing.canceled || meta.at <= existing.updatedAtMs) return state;
    const updated: MeetingRecord = {
      ...existing,
      title: body.title,
      startsAt: body.starts_at,
      endsAt: body.ends_at,
      ...optional('location', body.location),
      ...optional('description', body.description),
      ...optional('meetingLink', body.meeting_link),
      attendeeIds: [...body.attendee_user_ids],
      ...optional('autoCall', body.auto_call),
      updatedAtMs: meta.at,
    };
    return { ...state, meetings: { ...state.meetings, [body.meeting_id]: updated } };
  }
  const record: MeetingRecord = {
    meetingId: body.meeting_id,
    creatorAccountId: meta.senderAccountId,
    title: body.title,
    startsAt: body.starts_at,
    endsAt: body.ends_at,
    ...optional('location', body.location),
    ...optional('description', body.description),
    ...optional('meetingLink', body.meeting_link),
    attendeeIds: [...body.attendee_user_ids],
    ...optional('autoCall', body.auto_call),
    createdAtMs: meta.at,
    updatedAtMs: meta.at,
    canceled: false,
  };
  return { ...state, meetings: { ...state.meetings, [body.meeting_id]: record } };
}

function applyUpdate(
  state: MeetingState,
  body: MeetingUpdateBody,
  meta: MeetingEventMeta,
): MeetingState {
  const record = state.meetings[body.meeting_id];
  if (record === undefined) return state;
  // Creator-only; absorbing cancel; LWW guard.
  if (record.creatorAccountId !== meta.senderAccountId) return state;
  if (record.canceled || meta.at <= record.updatedAtMs) return state;

  let next: MeetingRecord = { ...record, updatedAtMs: meta.at };
  if (body.title !== undefined && body.title.trim().length > 0) next = { ...next, title: body.title };
  if (body.starts_at !== undefined) next = { ...next, startsAt: body.starts_at };
  if (body.ends_at !== undefined) next = { ...next, endsAt: body.ends_at };
  if (next.endsAt < next.startsAt) return state; // invalid time range → drop
  // Empty string clears the optional; absent leaves it unchanged.
  if (body.location !== undefined) next = setOrClear(next, 'location', body.location);
  if (body.description !== undefined) next = setOrClear(next, 'description', body.description);
  if (body.meeting_link !== undefined) next = setOrClear(next, 'meetingLink', body.meeting_link);
  if (body.attendee_user_ids_added !== undefined || body.attendee_user_ids_removed !== undefined) {
    const set = new Set(next.attendeeIds);
    for (const id of body.attendee_user_ids_added ?? []) set.add(id);
    for (const id of body.attendee_user_ids_removed ?? []) set.delete(id);
    next = { ...next, attendeeIds: [...set] };
  }
  return { ...state, meetings: { ...state.meetings, [body.meeting_id]: next } };
}

function applyCancel(
  state: MeetingState,
  body: MeetingCancelBody,
  meta: MeetingEventMeta,
): MeetingState {
  const record = state.meetings[body.meeting_id];
  if (record === undefined) return state;
  if (record.creatorAccountId !== meta.senderAccountId) return state;
  if (record.canceled) return state;
  // Absorbing regardless of LWW: an authentic creator cancel always sticks.
  const next: MeetingRecord = {
    ...record,
    canceled: true,
    ...optional('cancelReason', body.reason),
    updatedAtMs: Math.max(record.updatedAtMs, meta.at),
  };
  return { ...state, meetings: { ...state.meetings, [body.meeting_id]: next } };
}

function applyRsvp(
  state: MeetingState,
  body: MeetingRsvpBody,
  meta: MeetingEventMeta,
): MeetingState {
  const record = state.meetings[body.meeting_id];
  if (record === undefined || record.canceled || meta.senderAccountId.length === 0) return state;
  const existing = state.rsvps[body.meeting_id]?.[meta.senderAccountId];
  if (existing !== undefined && existing.votedAtMs > meta.at) return state; // LWW
  const rsvp: MeetingRsvp = {
    meetingId: body.meeting_id,
    voterAccountId: meta.senderAccountId,
    response: body.response,
    ...optional('note', body.note),
    votedAtMs: meta.at,
  };
  return {
    ...state,
    rsvps: {
      ...state.rsvps,
      [body.meeting_id]: { ...state.rsvps[body.meeting_id], [meta.senderAccountId]: rsvp },
    },
  };
}

/** Fold one authenticated meeting event into the state. */
export function reduceMeeting(
  state: MeetingState,
  event: MeetingEvent,
  meta: MeetingEventMeta,
): MeetingState {
  switch (event.kind) {
    case 'create':
      return applyCreate(state, event.body, meta);
    case 'update':
      return applyUpdate(state, event.body, meta);
    case 'rsvp':
      return applyRsvp(state, event.body, meta);
    case 'cancel':
      return applyCancel(state, event.body, meta);
  }
}

// ── Derived accessors (for the card) ─────────────────────────────────────────

export type MeetingStatus = 'upcoming' | 'inProgress' | 'past' | 'canceled';

export function meetingStatus(m: MeetingRecord, now: number): MeetingStatus {
  if (m.canceled) return 'canceled';
  if (now >= m.endsAt) return 'past';
  if (now >= m.startsAt) return 'inProgress';
  return 'upcoming';
}

export interface RsvpCounts {
  readonly yes: number;
  readonly no: number;
  readonly maybe: number;
}

export function rsvpCounts(state: MeetingState, meetingId: string): RsvpCounts {
  const byVoter = state.rsvps[meetingId] ?? {};
  const counts = { yes: 0, no: 0, maybe: 0 };
  for (const rsvp of Object.values(byVoter)) counts[rsvp.response] += 1;
  return counts;
}

export function myRsvp(
  state: MeetingState,
  meetingId: string,
  myAccountId: string,
): MeetingRsvpResponse | undefined {
  return state.rsvps[meetingId]?.[myAccountId]?.response;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function optional<K extends string, V>(key: K, value: V | undefined): Record<K, V> | Record<never, never> {
  return value !== undefined ? ({ [key]: value } as Record<K, V>) : {};
}

/** Empty string clears the field (delete the key); a non-empty string sets it. */
function setOrClear<K extends 'location' | 'description' | 'meetingLink'>(
  record: MeetingRecord,
  key: K,
  value: string,
): MeetingRecord {
  const next = { ...record };
  if (value.length === 0) {
    delete (next as Record<string, unknown>)[key];
  } else {
    (next as Record<string, unknown>)[key] = value;
  }
  return next;
}
